import { t } from '@lingui/core/macro';
import { colorSystem, Dot, Flexbox, Text, Tooltip } from '@luminovo/design-system';
import { formatDecimal } from '@luminovo/frontend-utils';
import React from 'react';

import { barScale, Row, ROW_HEIGHT, RowButton, Rows, Segment, Track, ZeroLine } from '../chartRows';
import { type ComparisonBarChartProps, type ComparisonBarDatum } from './ComparisonBarChart';
import { BASELINE_COLOR, BASELINE_TEXT_COLOR, formatPct, MONETARY_EPSILON } from './comparisonChartShared';
import { ComparisonTooltip } from './ComparisonTooltip';

type ChartView = 'volume' | 'priceChanges';

/** Volume sorts on the group's baseline, descending; price changes on the first visible reference's net, ascending. */
export function buildComparisonGroups(data: ComparisonBarDatum[], view: ChartView, visibleRefIndices?: number[]) {
    const groups = new Map<string, ComparisonBarDatum[]>();
    for (const datum of data) {
        if (visibleRefIndices && !visibleRefIndices.includes(datum.refIndex)) continue;
        const group = groups.get(datum.groupLabel) ?? [];
        group.push(datum);
        groups.set(datum.groupLabel, group);
    }
    for (const group of groups.values()) group.sort((a, b) => a.refIndex - b.refIndex);
    return [...groups.entries()].sort(([, a], [, b]) =>
        view === 'volume'
            ? b[0].baselineTotal - a[0].baselineTotal
            : a[0].increase + a[0].decrease - (b[0].increase + b[0].decrease),
    );
}

function signedValue(value: number, formatValue: (value: number) => string) {
    const sign = value > MONETARY_EPSILON ? '+' : value < -MONETARY_EPSILON ? '−' : '';
    return `${sign}${formatValue(Math.abs(value))}`;
}

export function ComparisonRows({
    data,
    view,
    formatValue = (value) => formatDecimal(value),
    visibleRefIndices,
    onBarClick,
    showNetAnnotation = true,
    baselineColor = BASELINE_COLOR,
}: ComparisonBarChartProps & { view: ChartView; baselineColor?: string }) {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [data]);

    const groups = React.useMemo(
        () => buildComparisonGroups(data, view, visibleRefIndices),
        [data, view, visibleRefIndices],
    );
    const visibleData = groups.flatMap(([, datums]) => datums);
    const multipleRefs = new Set(visibleData.map((datum) => datum.refIndex)).size > 1;
    // Scale to the bars that are drawn: one baseline bar per group, then each visible reference's.
    const scale = barScale(
        groups.flatMap(([, datums]) =>
            view === 'volume'
                ? [datums[0].baselineTotal, ...datums.map((datum) => datum.refTotal)].map((total) => ({
                      negative: 0,
                      positive: total,
                  }))
                : datums.map((datum) => ({ negative: datum.decrease, positive: datum.increase })),
        ),
    );

    // No reference matched a priced line, or the visible references have none.
    if (groups.length === 0) {
        return (
            <Flexbox style={{ flex: 1, minHeight: 200 }} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                <Text variant="body" color={colorSystem.neutral[7]}>
                    {t`No comparison data available.`}
                </Text>
            </Flexbox>
        );
    }

    return (
        <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Rows>
                {groups.map(([groupLabel, datums]) => {
                    const first = datums[0];
                    const grouped = view === 'volume' || multipleRefs;
                    const rows = [
                        ...(view === 'volume' ? [{ datum: first, baseline: true }] : []),
                        ...datums.map((datum) => ({ datum, baseline: false })),
                    ];
                    return (
                        <React.Fragment key={groupLabel}>
                            {grouped && (
                                <Flexbox
                                    gap="8px"
                                    style={{
                                        gridColumn: '1 / -1',
                                        minHeight: ROW_HEIGHT,
                                        padding: '0 16px',
                                        background: colorSystem.neutral[1],
                                        borderBottom: `1px solid ${colorSystem.neutral[2]}`,
                                    }}
                                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                                >
                                    <Text variant="body-small-semibold" color={colorSystem.neutral[8]}>
                                        {groupLabel}
                                    </Text>
                                    {first.groupSubLabel && (
                                        <Text variant="body-small" color={colorSystem.neutral[7]}>
                                            {first.groupSubLabel}
                                        </Text>
                                    )}
                                </Flexbox>
                            )}
                            {rows.map(({ datum, baseline }) => {
                                const net = datum.increase + datum.decrease;
                                const value = baseline ? datum.baselineTotal : datum.refTotal;
                                const label = grouped ? (baseline ? t`Baseline` : datum.refLabel) : groupLabel;
                                const pct = formatPct(net, datum.comparedBaselineTotal);
                                const formatted =
                                    view === 'volume'
                                        ? formatValue(value)
                                        : `${signedValue(net, formatValue)}${pct ? ` (${pct})` : ''}`;
                                const color = baseline ? baselineColor : datum.refColor;
                                const valueColor =
                                    view === 'volume'
                                        ? baseline
                                            ? BASELINE_TEXT_COLOR
                                            : datum.refTextColor
                                        : net < -MONETARY_EPSILON
                                          ? colorSystem.green[7]
                                          : net > MONETARY_EPSILON
                                            ? colorSystem.red[7]
                                            : colorSystem.neutral[7];
                                const segments =
                                    view === 'volume'
                                        ? [{ value, color }]
                                        : [
                                              { value: datum.decrease, color: colorSystem.green[4] },
                                              { value: datum.increase, color: colorSystem.red[4] },
                                          ];
                                const cells = (
                                    <>
                                        <Flexbox
                                            gap="6px"
                                            style={{ minWidth: 0, maxWidth: 320 }}
                                            sx={{ alignItems: 'center' }}
                                        >
                                            {grouped && <Dot color={color} />}
                                            <Text
                                                variant="body-small"
                                                color={colorSystem.neutral[8]}
                                                showEllipsis="ellipsisOnly"
                                            >
                                                {label}
                                            </Text>
                                            {!grouped && datum.groupSubLabel && (
                                                <Text
                                                    variant="body-small"
                                                    color={colorSystem.neutral[7]}
                                                    showEllipsis="ellipsisOnly"
                                                >
                                                    {datum.groupSubLabel}
                                                </Text>
                                            )}
                                        </Flexbox>
                                        <Track aria-hidden>
                                            {view === 'priceChanges' && <ZeroLine style={{ left: `${scale.zero}%` }} />}
                                            {segments
                                                .filter((segment) => segment.value !== 0)
                                                .map((segment, index) => (
                                                    <Segment
                                                        key={index}
                                                        data-chart-segment
                                                        style={{
                                                            left: `${segment.value < 0 ? scale.zero - scale.percent(segment.value) : scale.zero}%`,
                                                            width: `${scale.percent(segment.value)}%`,
                                                            background: segment.color,
                                                        }}
                                                    />
                                                ))}
                                        </Track>
                                        <Text
                                            variant="body-small"
                                            color={valueColor}
                                            tabularNums
                                            style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                                        >
                                            {view === 'volume' || showNetAnnotation ? formatted : ''}
                                        </Text>
                                    </>
                                );
                                const name = [
                                    groupLabel,
                                    datum.groupSubLabel,
                                    grouped ? label : datum.refLabel,
                                    formatted,
                                ]
                                    .filter(Boolean)
                                    .join(' ');
                                return (
                                    <Tooltip
                                        key={baseline ? 'baseline' : datum.refIndex}
                                        variant="white"
                                        disableMaxWidth
                                        describeChild
                                        disableInteractive
                                        placement="top"
                                        title={
                                            <ComparisonTooltip
                                                datums={datums}
                                                groupLabel={groupLabel}
                                                groupSubLabel={first.groupSubLabel}
                                                formatValue={formatValue}
                                            />
                                        }
                                    >
                                        {onBarClick ? (
                                            <RowButton
                                                type="button"
                                                aria-label={name}
                                                onClick={() => onBarClick(datum)}
                                            >
                                                {cells}
                                            </RowButton>
                                        ) : (
                                            // A role, since ARIA doesn't let a plain div take a name.
                                            <Row role="group" tabIndex={0} aria-label={name}>
                                                {cells}
                                            </Row>
                                        )}
                                    </Tooltip>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </Rows>
        </div>
    );
}
